import {
  IDateRange,
  BookingStatusEnum,
  OperationEnum,
  ProductTypeEnum,
  IDate,
  AccountTypeEnum,
  AgentTypeEnum,
  CurrencyEnum,
  IUserTokenData,
  ExternalBookingStatusEnum,
  BookingUpdatePermissionStatusEnum,
} from './types';
import {
  sleep,
  isIDateInIDateRange,
  stringToNumber,
  formatDate,
  stringToIDate,
  randomString,
} from '@/utils/index';
import request from '@/utils/request';
import { mockAgentSummariesCache } from './agent';
import { mockProductSummariesCache, mockProductsCache } from './product';
import { UserModule } from '@/store/modules/user';
import { mockInternalBookingInventoryEventFromBooking } from './inventory';
import {
  mockInternalNewInvoiceFromBooking,
  mockInternalUpdateInvoiceStatusFromBooking,
  mockInternalDeleteInvoiceFromBooking,
} from './invoice';
import { AppRoleType } from '@/utils/types';

// data type
export namespace search_bookings {
  export interface Request {
    agentIds?: string[];
    dateRange?: IDateRange;
    statuses: ExternalBookingStatusEnum[];
    eticketIssuingPermissionStatuses?: BookingUpdatePermissionStatusEnum[];
    balanceCreditDepositingPermissionStatuses?: BookingUpdatePermissionStatusEnum[];
  }

  export interface Booking {
    bookingId: string;
    submittedAt: string;
    agentId: string;
    agentName: string;
    status: ExternalBookingStatusEnum;
    eticketIssuingPermissionStatus: BookingUpdatePermissionStatusEnum;
    balanceCreditDepositingPermissionStatus: BookingUpdatePermissionStatusEnum;
  }

  export interface Response {
    bookings: Booking[];
  }
}

export namespace get_booking_detail {
  export interface Request {
    bookingId: string;
  }

  export interface Price {
    currency: string;
    listPrice: number;
    netPrice: number;
  }

  export interface Destination {
    address: string;
    recipient: string;
    contact: string;
    country: string;
  }

  export interface OrderUnit {
    minAmount?: number;
    maxAmount?: number;
    stepAmount?: number;
  }

  export interface TicketBlock {
    ticketNumberFrom: string;
    ticketNumberTo: string;
  }

  export interface Product {
    id: string;
    type: ProductTypeEnum;
    name: string;
    nameEn: string;
    categories?: string[];
    timezone: string;
    datePeriod?: IDateRange;
    fiscalYear: number;
    orderUnit?: OrderUnit;
    price: Price;
    amount: number;
    eticketUrls?: string[];
    ticketBlocks?: TicketBlock[];
    currency: string;
    totalPrice: number;
    eticketBookingId?: string;
    metas: Product.Meta[];
  }

  export namespace Product {
    export interface Meta {
      key: string;
      value: string;
    }
  }

  export interface Delivery {
    trackingNumber: string;
    trackingUrl: string;
  }

  export interface Order {
    destination: Destination;
    products: Product[];
    delivery?: Delivery;
    currency: string;
    totalPrice: number;
    documents: Order.OrderDocuments;
  }

  export namespace Order {
    export interface OrderDocuments {
      customInvoiceUrl?: string;
      shipmentChecklistUrl?: string;
      receiptUrl?: string;
    }
  }

  export interface BankTransferDetail {
    actualAmount: number;
    date: IDate;
    bankNumber: string;
    bankName: string;
    branchNumber: string;
    branchName: string;
    accountNumber: string;
    accountName: string;
    businessCode: string;
    accountCode: string;
    auxiliaryCode: string;
  }

  export interface Documents {
    invoiceUrl?: string;
    paymentReceiptUrl?: string;
  }

  export interface OperationLog {
    accountType: AccountTypeEnum;
    operator: string;
    operation: OperationEnum;
    note?: string;
    timestamp: string;
  }

  export interface Discrepancy {
    title: string;
    currency: string;
    amount: number;
  }

  export interface Response {
    bookingId: string;
    agentId: string;
    agentName: string;
    status: ExternalBookingStatusEnum;
    orders: Order[];
    discrepancies?: Discrepancy[];
    documents?: Documents;
    bankTransferDetail?: BankTransferDetail;
    operationLogs: OperationLog[];
    availableOperations: OperationEnum[];
    accountingSlipFinshed?: boolean;
    currency: string;
    totalPrice: number;
    eticketIssuingPermissionStatus?: BookingUpdatePermissionStatusEnum;
    balanceCreditDepositingPermissionStatus?: BookingUpdatePermissionStatusEnum;
    cumulativeDiscrepancies?: Discrepancy[];
    version: string;
  }
}

export namespace book {
  export interface Destination {
    address: string;
    recipient: string;
    contact: string;
    country: string;
  }

  export interface Product {
    id: string;
    name: string;
    amount: number;
  }

  export interface Order {
    destination: Destination;
    products: Product[];
  }

  export interface Request {
    orders: Order[];
    note?: string;
  }

  export interface Response {
    bookingId: string;
  }
}

export namespace update_booking {
  export interface InitiallyConfirm {
    discrepancies?: InitiallyConfirm.Discrepancy[];
  }

  export namespace InitiallyConfirm {
    export interface Discrepancy {
      title: string;
      currency: string;
      amount: number;
    }
  }

  export interface SupervisorConfirm {
    reject?: boolean;
  }

  export interface AccountingConfirm {
    reject?: boolean;
  }

  export interface PublishInvoice {}

  export interface NotifyPayment {
    receiptBlob?: string;
    contentType?: string;
  }

  export interface ConfirmPayment {
    actualAmount: number;
    date: IDate;
    bankNumber: string;
    bankName: string;
    branchNumber: string;
    branchName: string;
    accountNumber: string;
    accountName: string;
    businessCode: string;
    accountCode: string;
    auxiliaryCode: string;
  }

  export interface AnnotateAccountingSlipFinshed {
    revoke: boolean;
  }

  export interface RecordTicketNumbers {
    orders: RecordTicketNumbers.Order[];
  }

  export namespace RecordTicketNumbers {
    export interface Order {
      products: RecordTicketNumbers.Order.Product[];
    }

    export namespace Order {
      export interface Product {
        productId: string;
        ticketBlocks: RecordTicketNumbers.Order.Product.TicketBlock[];
      }

      export namespace Product {
        export interface TicketBlock {
          ticketNumberFrom: string;
          ticketNumberTo: string;
        }
      }
    }
  }

  export interface NotifyDelivery {
    deliveries: NotifyDelivery.Delivery[];
  }

  export namespace NotifyDelivery {
    export interface Delivery {
      trackingNumber: string;
      trackingUrl: string;
    }
  }

  export interface ConfirmDelivery {}

  export interface Cancel {}

  export interface PermitEticketIssuing {}

  export interface PermitBalanceCreditDepositing {}

  export interface Request {
    bookingId: string;
    operatorName: string;
    initiallyConfirm?: InitiallyConfirm;
    supervisorConfirm?: SupervisorConfirm;
    accountingConfirm?: AccountingConfirm;
    publishInvoice?: PublishInvoice;
    notifyPayment?: NotifyPayment;
    confirmPayment?: ConfirmPayment;
    annotateAccountingSlipFinshed?: AnnotateAccountingSlipFinshed;
    recordTicketNumbers?: RecordTicketNumbers;
    notifyDelivery?: NotifyDelivery;
    confirmDelivery?: ConfirmDelivery;
    permitEticketIssuing?: PermitEticketIssuing;
    permitBalanceCreditDepositing?: PermitBalanceCreditDepositing;
    cancel?: Cancel;
    note?: string;
    version: string;
  }

  export interface Response {}
}

// mock
const useMock = process.env.VUE_APP_API_USE_MOCK === 'true';

const bookingStatusToExternalBookingStatus = (
  bookingStatus: BookingStatusEnum,
  isAgent = false
) => {
  if (!isAgent) {
    switch (bookingStatus as BookingStatusEnum) {
      case BookingStatusEnum.UNKNOWN_BOOKING_STATUS:
        return ExternalBookingStatusEnum.UNKNOWN_EXTERNAL_BOOKING_STATUS;
      case BookingStatusEnum.SUBMITTED:
        return ExternalBookingStatusEnum.EXTERNAL_OCTB_SUBMITTED;
      case BookingStatusEnum.CONFIRMED_INIT:
        return ExternalBookingStatusEnum.EXTERNAL_OCTB_CONFIRMED_INIT;
      case BookingStatusEnum.CONFIRMED_SUP:
        return ExternalBookingStatusEnum.EXTERNAL_OCTB_CONFIRMED_SUP;
      case BookingStatusEnum.CONFIRMED_ACCOUNTING:
        return ExternalBookingStatusEnum.EXTERNAL_OCTB_CONFIRMED_ACCOUNTING;
      case BookingStatusEnum.CONFIRMED:
        return ExternalBookingStatusEnum.EXTERNAL_OCTB_CONFIRMED;
      case BookingStatusEnum.PAYMENT_NOTIFIED:
        return ExternalBookingStatusEnum.EXTERNAL_OCTB_PAYMENT_NOTIFIED;
      case BookingStatusEnum.PAYMENT_CONFIRMED:
        return ExternalBookingStatusEnum.EXTERNAL_OCTB_PAYMENT_CONFIRMED;
      case BookingStatusEnum.DELIVERY_PREPARING:
        return ExternalBookingStatusEnum.EXTERNAL_OCTB_DELIVERY_PREPARING;
      case BookingStatusEnum.DELIVERY_STARTED:
        return ExternalBookingStatusEnum.EXTERNAL_OCTB_DELIVERY_STARTED;
      case BookingStatusEnum.DELIVERY_FINISHED_ETPENDING:
        return ExternalBookingStatusEnum.EXTERNAL_OCTB_DONE_ETPENDING;
      case BookingStatusEnum.DELIVERY_FINISHED:
        return ExternalBookingStatusEnum.EXTERNAL_OCTB_DONE;
      case BookingStatusEnum.CANCELED:
        return ExternalBookingStatusEnum.EXTERNAL_OCTB_CANCELED;
      default:
        return ExternalBookingStatusEnum.UNKNOWN_EXTERNAL_BOOKING_STATUS;
    }
  } else {
    switch (bookingStatus as BookingStatusEnum) {
      case BookingStatusEnum.UNKNOWN_BOOKING_STATUS:
        return ExternalBookingStatusEnum.UNKNOWN_EXTERNAL_BOOKING_STATUS;
      case BookingStatusEnum.SUBMITTED:
      case BookingStatusEnum.CONFIRMED_INIT:
      case BookingStatusEnum.CONFIRMED_SUP:
      case BookingStatusEnum.CONFIRMED_ACCOUNTING:
        return ExternalBookingStatusEnum.EXTERNAL_AGT_CONFIRMATION_WAITING;
      case BookingStatusEnum.CONFIRMED:
        return ExternalBookingStatusEnum.EXTERNAL_AGT_PAYMENT_WAITING;
      case BookingStatusEnum.PAYMENT_NOTIFIED:
        return ExternalBookingStatusEnum.EXTERNAL_AGT_PAYMENT_CONFIRMATION_WAITING;
      case BookingStatusEnum.PAYMENT_CONFIRMED:
      case BookingStatusEnum.DELIVERY_PREPARING:
        return ExternalBookingStatusEnum.EXTERNAL_AGT_DELIVERY_WAITING;
      case BookingStatusEnum.DELIVERY_STARTED:
        return ExternalBookingStatusEnum.EXTERNAL_AGT_DELIVERY_STARTED;
      case BookingStatusEnum.DELIVERY_FINISHED_ETPENDING:
        return ExternalBookingStatusEnum.EXTERNAL_AGT_DONE_ETPENDING;
      case BookingStatusEnum.DELIVERY_FINISHED:
        return ExternalBookingStatusEnum.EXTERNAL_AGT_DONE;
      case BookingStatusEnum.CANCELED:
        return ExternalBookingStatusEnum.EXTERNAL_AGT_CANCELED;
      default:
        return ExternalBookingStatusEnum.UNKNOWN_EXTERNAL_BOOKING_STATUS;
    }
  }
};

const externalBookingStatusToBookingStatuses = (
  bookingStatus: ExternalBookingStatusEnum
) => {
  switch (bookingStatus as ExternalBookingStatusEnum) {
    case ExternalBookingStatusEnum.UNKNOWN_EXTERNAL_BOOKING_STATUS:
      return [BookingStatusEnum.UNKNOWN_BOOKING_STATUS];
    case ExternalBookingStatusEnum.EXTERNAL_OCTB_SUBMITTED:
      return [BookingStatusEnum.SUBMITTED];
    case ExternalBookingStatusEnum.EXTERNAL_OCTB_CONFIRMED_INIT:
      return [BookingStatusEnum.CONFIRMED_INIT];
    case ExternalBookingStatusEnum.EXTERNAL_OCTB_CONFIRMED_SUP:
      return [BookingStatusEnum.CONFIRMED_SUP];
    case ExternalBookingStatusEnum.EXTERNAL_OCTB_CONFIRMED_ACCOUNTING:
      return [BookingStatusEnum.CONFIRMED_ACCOUNTING];
    case ExternalBookingStatusEnum.EXTERNAL_OCTB_CONFIRMED:
      return [BookingStatusEnum.CONFIRMED];
    case ExternalBookingStatusEnum.EXTERNAL_OCTB_PAYMENT_NOTIFIED:
      return [BookingStatusEnum.PAYMENT_NOTIFIED];
    case ExternalBookingStatusEnum.EXTERNAL_OCTB_PAYMENT_CONFIRMED:
      return [BookingStatusEnum.PAYMENT_CONFIRMED];
    case ExternalBookingStatusEnum.EXTERNAL_OCTB_DELIVERY_PREPARING:
      return [BookingStatusEnum.DELIVERY_PREPARING];
    case ExternalBookingStatusEnum.EXTERNAL_OCTB_DELIVERY_STARTED:
      return [BookingStatusEnum.DELIVERY_STARTED];
    case ExternalBookingStatusEnum.EXTERNAL_OCTB_DONE_ETPENDING:
      return [BookingStatusEnum.DELIVERY_FINISHED_ETPENDING];
    case ExternalBookingStatusEnum.EXTERNAL_OCTB_DONE:
      return [BookingStatusEnum.DELIVERY_FINISHED];
    case ExternalBookingStatusEnum.EXTERNAL_OCTB_CANCELED:
      return [BookingStatusEnum.CANCELED];
    case ExternalBookingStatusEnum.EXTERNAL_AGT_CONFIRMATION_WAITING:
      return [
        BookingStatusEnum.SUBMITTED,
        BookingStatusEnum.CONFIRMED_INIT,
        BookingStatusEnum.CONFIRMED_SUP,
        BookingStatusEnum.CONFIRMED_ACCOUNTING,
      ];
    case ExternalBookingStatusEnum.EXTERNAL_AGT_PAYMENT_WAITING:
      return [BookingStatusEnum.CONFIRMED];
    case ExternalBookingStatusEnum.EXTERNAL_AGT_PAYMENT_CONFIRMATION_WAITING:
      return [BookingStatusEnum.PAYMENT_NOTIFIED];
    case ExternalBookingStatusEnum.EXTERNAL_AGT_DELIVERY_WAITING:
      return [
        BookingStatusEnum.PAYMENT_CONFIRMED,
        BookingStatusEnum.DELIVERY_PREPARING,
      ];
    case ExternalBookingStatusEnum.EXTERNAL_AGT_DELIVERY_STARTED:
      return [BookingStatusEnum.DELIVERY_STARTED];
    case ExternalBookingStatusEnum.EXTERNAL_AGT_DONE_ETPENDING:
      return [BookingStatusEnum.DELIVERY_FINISHED_ETPENDING];
    case ExternalBookingStatusEnum.EXTERNAL_AGT_DONE:
      return [BookingStatusEnum.DELIVERY_FINISHED];
    case ExternalBookingStatusEnum.EXTERNAL_AGT_CANCELED:
      return [BookingStatusEnum.CANCELED];
    default:
      return [BookingStatusEnum.UNKNOWN_BOOKING_STATUS];
  }
};

const mockNewBookingDetail = (
  bookingId: string,
  status: BookingStatusEnum,
  orders?: book.Order[]
) => {
  const now = new Date();
  const agent = Array.from(mockAgentSummariesCache.values()).filter(
    a => (a.type as AgentTypeEnum) === AgentTypeEnum.OVERSEAS_BUYOUT
  )[0];

  const detail: get_booking_detail.Response & WithInternalStatus = {
    bookingId: bookingId,
    agentId: agent.id,
    agentName: agent.name,
    status: bookingStatusToExternalBookingStatus(status),
    internalStatus: status,
    orders: [],
    operationLogs: [
      {
        accountType: AccountTypeEnum.AGT,
        operator: agent.name,
        operation: OperationEnum.SUBMIT,
        timestamp: `${now.toLocaleString()}`,
      },
    ],
    availableOperations: [
      OperationEnum.INITIALLY_CONFIRM,
      OperationEnum.CANCEL,
    ],
    currency: CurrencyEnum.JPY,
    totalPrice: 0,
    version: `${now.getTime()}`,
  };
  if (orders && orders.length > 0) {
    orders.forEach(o => {
      const order: get_booking_detail.Order = {
        destination: o.destination,
        products: [],
        currency: mockProductSummariesCache.get(o.products[0].id)!.price
          .currency,
        totalPrice: 0,
        documents: {},
      };
      o.products.forEach(p => {
        const product: get_booking_detail.Product = {
          id: p.id,
          type: mockProductSummariesCache.get(p.id)!.type,
          name: p.name,
          nameEn: mockProductSummariesCache.get(p.id)!.nameEn,
          categories: mockProductSummariesCache.get(p.id)!.categories,
          timezone: `${now.getTimezoneOffset()}`,
          fiscalYear: mockProductSummariesCache.get(p.id)!.fiscalYear,
          orderUnit: mockProductSummariesCache.get(p.id)!.orderUnit,
          price: mockProductSummariesCache.get(p.id)!.price,
          amount: p.amount,
          currency: mockProductSummariesCache.get(p.id)!.price.currency,
          totalPrice:
            p.amount * mockProductSummariesCache.get(p.id)!.price.netPrice,
          metas: mockProductsCache.get(p.id)!.metas,
        };
        if ((product.type as ProductTypeEnum) === ProductTypeEnum.E_TICKET) {
          product.eticketUrls = [`dummy.eticket.url/${product.id}`];
          product.eticketBookingId = `dummyEticketBookingId-${product.id}`;
        }
        order.products.push(product);
        order.totalPrice += product.totalPrice;
      });
      detail.orders.push(order);
      detail.totalPrice += order.totalPrice;
    });
  } else {
    const product = Array.from(mockProductSummariesCache.values()).filter(
      p => (p.type as ProductTypeEnum) === ProductTypeEnum.REAL_TICKET
    )[0];
    const amount = 1;
    const totalPrice = amount * product.price.netPrice;
    detail.orders.push({
      destination: {
        country: 'dummyCountry',
        address: 'dummyAddress',
        recipient: 'dummyRecipient',
        contact: 'dummyContact',
      },
      products: [
        {
          id: product.id,
          type: product.type,
          name: product.name,
          nameEn: product.nameEn,
          timezone: `${now.getTimezoneOffset()}`,
          fiscalYear: product.fiscalYear,
          orderUnit: product.orderUnit,
          price: product.price,
          amount: amount,
          currency: product.price.currency,
          totalPrice: totalPrice,
          metas: mockProductsCache.get(product.id)!.metas,
        },
      ],
      currency: product.price.currency,
      totalPrice: totalPrice,
      documents: {},
    });
    detail.totalPrice = totalPrice;
  }

  return detail;
};

export type WithInternalStatus = {
  internalStatus: BookingStatusEnum;
};

const mockInitialBookingDetailsCache = () => {
  const details: (get_booking_detail.Response & WithInternalStatus)[] = [];
  Object.values(BookingStatusEnum).forEach(status => {
    if ((status as BookingStatusEnum) === BookingStatusEnum.SUBMITTED) {
      details.push(mockNewBookingDetail(`bookingId-${status}`, status));
    }
  });
  return new Map(details.map(d => [d.bookingId, d]));
};
export const mockBookingDetailsCache = mockInitialBookingDetailsCache();

const mockSearchBookings = (req: search_bookings.Request) => {
  const bookings: (search_bookings.Booking & { agentId: string })[] = [];
  mockBookingDetailsCache.forEach(b => {
    if (
      (!req.dateRange ||
        isIDateInIDateRange(
          stringToIDate(formatDate(new Date(stringToNumber(b.version)))),
          req.dateRange,
          true
        )) &&
      (!req.statuses ||
        req.statuses.length <= 0 ||
        req.statuses.some(es =>
          externalBookingStatusToBookingStatuses(es).some(
            s =>
              (s as BookingStatusEnum) ===
              (b.internalStatus as BookingStatusEnum)
          )
        ))
    ) {
      bookings.push({
        bookingId: b.bookingId,
        submittedAt: b.operationLogs[0].timestamp,
        agentId: b.agentId,
        agentName: b.agentName,
        status: bookingStatusToExternalBookingStatus(
          b.internalStatus,
          UserModule.roles.some(r => (r as AppRoleType) === AppRoleType.AGENT)
        ),
        eticketIssuingPermissionStatus:
          b.eticketIssuingPermissionStatus ||
          BookingUpdatePermissionStatusEnum.NOT_COMPATIBLE,
        balanceCreditDepositingPermissionStatus:
          b.balanceCreditDepositingPermissionStatus ||
          BookingUpdatePermissionStatusEnum.NOT_COMPATIBLE,
      });
    }
  });

  return {
    bookings: bookings,
  } as search_bookings.Response;
};

const mockGetBookingDetail = (req: get_booking_detail.Request) => {
  const res = JSON.parse(
    JSON.stringify(mockBookingDetailsCache.get(req.bookingId)!)
  ) as get_booking_detail.Response & WithInternalStatus;
  const isAgent = UserModule.roles.some(
    r => (r as AppRoleType) === AppRoleType.AGENT
  );
  res.status = bookingStatusToExternalBookingStatus(
    res.internalStatus,
    isAgent
  );
  delete res.internalStatus;
  return res;
};

const mockCreateBooking = (req: book.Request) => {
  const bookingId = randomString(4);
  const newBooking = mockNewBookingDetail(
    bookingId,
    BookingStatusEnum.SUBMITTED,
    req.orders
  );
  if (req.note) {
    newBooking.operationLogs[0].note = req.note;
  }
  mockBookingDetailsCache.set(bookingId, newBooking);
  return {
    bookingId: bookingId,
  } as book.Response;
};

const updateBookingForCancel = (
  detail: get_booking_detail.Response & WithInternalStatus,
  req: update_booking.Request
) => {
  const now = new Date();
  detail.operationLogs.push({
    accountType: (JSON.parse(UserModule.token) as IUserTokenData).accountType,
    operator: req.operatorName,
    operation: OperationEnum.CANCEL,
    timestamp: `${now.toLocaleString()}`,
    note: req.note,
  });
  detail.availableOperations = [];
  detail.status = ExternalBookingStatusEnum.EXTERNAL_OCTB_CANCELED;
  detail.internalStatus = BookingStatusEnum.CANCELED;
  detail.version = `${now.getTime()}`;

  // delete invoice for booking
  mockInternalDeleteInvoiceFromBooking(detail);
};

const updateBookingForInitiallyConfirm = (
  detail: get_booking_detail.Response & WithInternalStatus,
  req: update_booking.Request
) => {
  const now = new Date();
  if (
    req.initiallyConfirm &&
    req.initiallyConfirm.discrepancies &&
    req.initiallyConfirm.discrepancies.length > 0
  ) {
    detail.discrepancies = req.initiallyConfirm.discrepancies;
    detail.discrepancies.forEach(d => {
      detail.totalPrice += d.amount;
    });
  }
  detail.operationLogs.push({
    accountType: AccountTypeEnum.OCTB,
    operator: req.operatorName,
    operation: OperationEnum.INITIALLY_CONFIRM,
    timestamp: `${now.toLocaleString()}`,
    note: req.note,
  });
  detail.availableOperations = [
    OperationEnum.SUPERVISOR_CONFIRM,
    OperationEnum.SUPERVISOR_CONFIRM_REJECT,
    OperationEnum.CANCEL,
  ];
  detail.status = ExternalBookingStatusEnum.EXTERNAL_OCTB_CONFIRMED_INIT;
  detail.internalStatus = BookingStatusEnum.CONFIRMED_INIT;
  detail.version = `${now.getTime()}`;
};

const updateBookingForSupervisorConfirm = (
  detail: get_booking_detail.Response & WithInternalStatus,
  req: update_booking.Request
) => {
  const now = new Date();
  if (req.supervisorConfirm && req.supervisorConfirm.reject) {
    (detail.discrepancies || []).forEach(d => (detail.totalPrice -= d.amount));
    delete detail.discrepancies;

    detail.operationLogs.push({
      accountType: AccountTypeEnum.OCTB,
      operator: req.operatorName,
      operation: OperationEnum.SUPERVISOR_CONFIRM_REJECT,
      timestamp: `${now.toLocaleString()}`,
      note: req.note,
    });
    detail.availableOperations = [
      OperationEnum.INITIALLY_CONFIRM,
      OperationEnum.CANCEL,
    ];
    detail.status = ExternalBookingStatusEnum.EXTERNAL_OCTB_SUBMITTED;
    detail.internalStatus = BookingStatusEnum.SUBMITTED;
  } else {
    detail.operationLogs.push({
      accountType: AccountTypeEnum.OCTB,
      operator: req.operatorName,
      operation: OperationEnum.SUPERVISOR_CONFIRM,
      timestamp: `${now.toLocaleString()}`,
      note: req.note,
    });
    detail.availableOperations = [
      OperationEnum.ACCOUNTING_CONFIRM,
      OperationEnum.ACCOUNTING_CONFIRM_REJECT,
      OperationEnum.CANCEL,
    ];
    detail.status = ExternalBookingStatusEnum.EXTERNAL_OCTB_CONFIRMED_SUP;
    detail.internalStatus = BookingStatusEnum.SUBMITTED;
  }
  detail.version = `${now.getTime()}`;
};

const updateBookingForAccountingConfirm = (
  detail: get_booking_detail.Response & WithInternalStatus,
  req: update_booking.Request
) => {
  const now = new Date();
  if (req.accountingConfirm && req.accountingConfirm.reject) {
    (detail.discrepancies || []).forEach(d => (detail.totalPrice -= d.amount));
    delete detail.discrepancies;

    detail.operationLogs.push({
      accountType: AccountTypeEnum.OCTB,
      operator: req.operatorName,
      operation: OperationEnum.ACCOUNTING_CONFIRM_REJECT,
      timestamp: `${now.toLocaleString()}`,
      note: req.note,
    });
    detail.availableOperations = [
      OperationEnum.INITIALLY_CONFIRM,
      OperationEnum.CANCEL,
    ];
    detail.status = ExternalBookingStatusEnum.EXTERNAL_OCTB_SUBMITTED;
    detail.internalStatus = BookingStatusEnum.SUBMITTED;
  } else {
    detail.documents = detail.documents || ({} as get_booking_detail.Documents);
    detail.documents.invoiceUrl = 'dummy.invoice.url';
    detail.operationLogs.push({
      accountType: AccountTypeEnum.OCTB,
      operator: req.operatorName,
      operation: OperationEnum.ACCOUNTING_CONFIRM,
      timestamp: `${now.toLocaleString()}`,
      note: req.note,
    });
    detail.availableOperations = [
      OperationEnum.PUBLISH_INVOICE,
      OperationEnum.CANCEL,
    ];
    detail.status =
      ExternalBookingStatusEnum.EXTERNAL_OCTB_CONFIRMED_ACCOUNTING;
    detail.internalStatus = BookingStatusEnum.CONFIRMED_ACCOUNTING;

    // create invoice from booking
    mockInternalNewInvoiceFromBooking(
      mockBookingDetailsCache.get(detail.bookingId)!
    );
  }
  detail.version = `${now.getTime()}`;
};

const updateBookingForPublishInvoice = (
  detail: get_booking_detail.Response & WithInternalStatus,
  req: update_booking.Request
) => {
  const now = new Date();
  detail.operationLogs.push({
    accountType: AccountTypeEnum.OCTB,
    operator: req.operatorName,
    operation: OperationEnum.PUBLISH_INVOICE,
    timestamp: `${now.toLocaleString()}`,
    note: req.note,
  });
  detail.availableOperations = [
    OperationEnum.NOTIFY_PAYMENT,
    OperationEnum.CANCEL,
    detail.accountingSlipFinshed
      ? OperationEnum.ANNOTATE_ACCOUNTING_SLIP_FINSHED_REVOKE
      : OperationEnum.ANNOTATE_ACCOUNTING_SLIP_FINSHED,
  ];
  detail.status = ExternalBookingStatusEnum.EXTERNAL_OCTB_CONFIRMED;
  detail.internalStatus = BookingStatusEnum.CONFIRMED;
  detail.version = `${now.getTime()}`;
};

const updateBookingForNotifyPayment = (
  detail: get_booking_detail.Response & WithInternalStatus,
  req: update_booking.Request
) => {
  const now = new Date();
  detail.documents!.paymentReceiptUrl = 'payment.receipt.url';
  detail.operationLogs.push({
    accountType: AccountTypeEnum.AGT,
    operator: req.operatorName,
    operation: OperationEnum.NOTIFY_PAYMENT,
    timestamp: `${now.toLocaleString()}`,
    note: req.note,
  });
  detail.accountingSlipFinshed = false;
  detail.availableOperations = [OperationEnum.CONFIRM_PAYMENT];
  detail.status = ExternalBookingStatusEnum.EXTERNAL_OCTB_PAYMENT_NOTIFIED;
  detail.internalStatus = BookingStatusEnum.PAYMENT_NOTIFIED;
  detail.version = `${now.getTime()}`;
};

const updateBookingForConfirmPayment = (
  detail: get_booking_detail.Response & WithInternalStatus,
  req: update_booking.Request
) => {
  const now = new Date();
  detail.bankTransferDetail = {
    actualAmount: req.confirmPayment!.actualAmount,
    date: req.confirmPayment!.date,
    bankNumber: req.confirmPayment!.bankNumber,
    bankName: req.confirmPayment!.bankName,
    branchNumber: req.confirmPayment!.branchNumber,
    branchName: req.confirmPayment!.branchName,
    accountNumber: req.confirmPayment!.accountNumber,
    accountName: req.confirmPayment!.accountName,
    businessCode: req.confirmPayment!.businessCode,
    accountCode: req.confirmPayment!.accountCode,
    auxiliaryCode: req.confirmPayment!.auxiliaryCode,
  };
  detail.operationLogs.push({
    accountType: AccountTypeEnum.OCTB,
    operator: req.operatorName,
    operation: OperationEnum.CONFIRM_PAYMENT,
    timestamp: `${now.toLocaleString()}`,
    note: req.note,
  });
  detail.availableOperations = [
    OperationEnum.RECORD_TICKET_NUMBERS,
    OperationEnum.ANNOTATE_ACCOUNTING_SLIP_FINSHED,
  ];
  detail.status = ExternalBookingStatusEnum.EXTERNAL_OCTB_PAYMENT_CONFIRMED;
  detail.internalStatus = BookingStatusEnum.PAYMENT_CONFIRMED;
  detail.version = `${now.getTime()}`;

  // update invoice status for booking
  mockInternalUpdateInvoiceStatusFromBooking(detail);
};

const updateBookingForAnnotateAccountingSlipFinshed = (
  detail: get_booking_detail.Response & WithInternalStatus,
  req: update_booking.Request
) => {
  const now = new Date();
  if (
    req.annotateAccountingSlipFinshed &&
    req.annotateAccountingSlipFinshed!.revoke
  ) {
    detail.accountingSlipFinshed = false;
    detail.availableOperations = detail.availableOperations
      .filter(
        o =>
          (o as OperationEnum) !==
          OperationEnum.ANNOTATE_ACCOUNTING_SLIP_FINSHED
      )
      .concat(OperationEnum.ANNOTATE_ACCOUNTING_SLIP_FINSHED_REVOKE);
    detail.operationLogs.push({
      accountType: AccountTypeEnum.OCTB,
      operator: req.operatorName,
      operation: OperationEnum.ANNOTATE_ACCOUNTING_SLIP_FINSHED_REVOKE,
      timestamp: `${now.toLocaleString()}`,
      note: req.note,
    });
  } else {
    detail.accountingSlipFinshed = true;
    detail.availableOperations = detail.availableOperations
      .filter(
        o =>
          (o as OperationEnum) !==
          OperationEnum.ANNOTATE_ACCOUNTING_SLIP_FINSHED_REVOKE
      )
      .concat(OperationEnum.ANNOTATE_ACCOUNTING_SLIP_FINSHED);
    detail.operationLogs.push({
      accountType: AccountTypeEnum.OCTB,
      operator: req.operatorName,
      operation: OperationEnum.ANNOTATE_ACCOUNTING_SLIP_FINSHED,
      timestamp: `${now.toLocaleString()}`,
      note: req.note,
    });
  }
  detail.version = `${now.getTime()}`;
};

const updateBookingForPermitEticketIssuing = (
  detail: get_booking_detail.Response & WithInternalStatus,
  req: update_booking.Request
) => {
  const now = new Date();
  detail.eticketIssuingPermissionStatus =
    BookingUpdatePermissionStatusEnum.PERMITTED;
  detail.operationLogs.push({
    accountType: AccountTypeEnum.OCTB,
    operator: req.operatorName,
    operation: OperationEnum.PERMIT_ETICKET_ISSUING,
    timestamp: `${now.toLocaleString()}`,
    note: req.note,
  });
  detail.version = `${now.getTime()}`;
};

const updateBookingForPermitBalanceCreditDepositing = (
  detail: get_booking_detail.Response & WithInternalStatus,
  req: update_booking.Request
) => {
  const now = new Date();
  detail.balanceCreditDepositingPermissionStatus =
    BookingUpdatePermissionStatusEnum.PERMITTED;
  detail.operationLogs.push({
    accountType: AccountTypeEnum.OCTB,
    operator: req.operatorName,
    operation: OperationEnum.PERMIT_BALANCE_CREDIT_DEPOSITING,
    timestamp: `${now.toLocaleString()}`,
    note: req.note,
  });
  detail.version = `${now.getTime()}`;
};

const updateBookingForRecordTicketNumbers = (
  detail: get_booking_detail.Response & WithInternalStatus,
  req: update_booking.Request
) => {
  mockInternalBookingInventoryEventFromBooking(
    req,
    detail.bookingId,
    mockBookingDetailsCache.get(detail.bookingId)!.agentId
  );
  const now = new Date();
  detail.operationLogs.push({
    accountType: AccountTypeEnum.OCTB,
    operator: req.operatorName,
    operation: OperationEnum.RECORD_TICKET_NUMBERS,
    timestamp: `${now.toLocaleString()}`,
    note: req.note,
  });
  req.recordTicketNumbers!.orders.forEach((reqOrder, orderIndex) => {
    const order = detail.orders[orderIndex];
    reqOrder.products.forEach(reqProduct => {
      const product = order.products.find(p => p.id === reqProduct.productId);
      if (product) {
        product.ticketBlocks = reqProduct.ticketBlocks.map(b => ({
          ticketNumberFrom: b.ticketNumberFrom,
          ticketNumberTo: b.ticketNumberTo,
        }));
      }
    });
  });
  detail.availableOperations = [OperationEnum.NOTIFY_DELIVERY].concat(
    detail.accountingSlipFinshed
      ? OperationEnum.ANNOTATE_ACCOUNTING_SLIP_FINSHED_REVOKE
      : OperationEnum.ANNOTATE_ACCOUNTING_SLIP_FINSHED
  );
  detail.status = ExternalBookingStatusEnum.EXTERNAL_OCTB_DELIVERY_PREPARING;
  detail.internalStatus = BookingStatusEnum.DELIVERY_PREPARING;
  detail.version = `${now.getTime()}`;
};

const updateBookingForNotifyDelivery = (
  detail: get_booking_detail.Response & WithInternalStatus,
  req: update_booking.Request
) => {
  if (detail.orders.length !== req.notifyDelivery!.deliveries.length) {
    throw new Error(`mock updateBookingForNotifyDelivery: size not equal`);
  }
  const size = detail.orders.length;

  const now = new Date();
  for (let i = 0; i < size; i++) {
    detail.orders[i].delivery = {
      trackingNumber: req.notifyDelivery!.deliveries[i].trackingNumber,
      trackingUrl: req.notifyDelivery!.deliveries[i].trackingUrl,
    };
  }
  detail.operationLogs.push({
    accountType: AccountTypeEnum.OCTB,
    operator: req.operatorName,
    operation: OperationEnum.NOTIFY_DELIVERY,
    timestamp: `${now.toLocaleString()}`,
    note: req.note,
  });
  detail.availableOperations = [OperationEnum.CONFIRM_DELIVERY].concat(
    detail.accountingSlipFinshed
      ? OperationEnum.ANNOTATE_ACCOUNTING_SLIP_FINSHED_REVOKE
      : OperationEnum.ANNOTATE_ACCOUNTING_SLIP_FINSHED
  );
  detail.status = ExternalBookingStatusEnum.EXTERNAL_OCTB_DELIVERY_STARTED;
  detail.internalStatus = BookingStatusEnum.DELIVERY_STARTED;
  detail.version = `${now.getTime()}`;
};

const updateBookingForConfirmDelivery = (
  detail: get_booking_detail.Response & WithInternalStatus,
  req: update_booking.Request
) => {
  const now = new Date();
  detail.operationLogs.push({
    accountType: AccountTypeEnum.AGT,
    operator: req.operatorName,
    operation: OperationEnum.CONFIRM_DELIVERY,
    timestamp: `${now.toLocaleString()}`,
    note: req.note,
  });
  detail.availableOperations = [
    detail.accountingSlipFinshed
      ? OperationEnum.ANNOTATE_ACCOUNTING_SLIP_FINSHED_REVOKE
      : OperationEnum.ANNOTATE_ACCOUNTING_SLIP_FINSHED,
  ];
  detail.status = ExternalBookingStatusEnum.EXTERNAL_OCTB_DONE;
  detail.internalStatus = BookingStatusEnum.DELIVERY_FINISHED;
  detail.version = `${now.getTime()}`;
};

const mockUpdateBooking = (req: update_booking.Request) => {
  const detail = mockBookingDetailsCache.get(req.bookingId);
  if (detail) {
    if (detail.version !== req.version) {
      throw new Error(
        `mock version not matched, req: ${req.version}, actual: ${detail.version}`
      );
    }
    if (req.cancel) {
      updateBookingForCancel(detail, req);
    } else if (
      (detail.internalStatus as BookingStatusEnum) ===
        BookingStatusEnum.SUBMITTED &&
      req.initiallyConfirm
    ) {
      updateBookingForInitiallyConfirm(detail, req);
    } else if (
      (detail.internalStatus as BookingStatusEnum) ===
        BookingStatusEnum.CONFIRMED_INIT &&
      req.supervisorConfirm
    ) {
      updateBookingForSupervisorConfirm(detail, req);
    } else if (
      (detail.internalStatus as BookingStatusEnum) ===
        BookingStatusEnum.CONFIRMED_SUP &&
      req.accountingConfirm
    ) {
      updateBookingForAccountingConfirm(detail, req);
    } else if (
      (detail.internalStatus as BookingStatusEnum) ===
        BookingStatusEnum.CONFIRMED_ACCOUNTING &&
      req.publishInvoice
    ) {
      updateBookingForPublishInvoice(detail, req);
    } else if (
      (detail.internalStatus as BookingStatusEnum) ===
        BookingStatusEnum.CONFIRMED &&
      req.notifyPayment
    ) {
      updateBookingForNotifyPayment(detail, req);
    } else if (
      (detail.internalStatus as BookingStatusEnum) ===
        BookingStatusEnum.PAYMENT_NOTIFIED &&
      req.confirmPayment
    ) {
      updateBookingForConfirmPayment(detail, req);
    } else if (req.annotateAccountingSlipFinshed) {
      updateBookingForAnnotateAccountingSlipFinshed(detail, req);
    } else if (req.permitEticketIssuing) {
      updateBookingForPermitEticketIssuing(detail, req);
    } else if (req.permitBalanceCreditDepositing) {
      updateBookingForPermitBalanceCreditDepositing(detail, req);
    } else if (
      (detail.internalStatus as BookingStatusEnum) ===
        BookingStatusEnum.PAYMENT_CONFIRMED &&
      req.recordTicketNumbers
    ) {
      updateBookingForRecordTicketNumbers(detail, req);
    } else if (
      (detail.internalStatus as BookingStatusEnum) ===
        BookingStatusEnum.DELIVERY_PREPARING &&
      req.notifyDelivery
    ) {
      updateBookingForNotifyDelivery(detail, req);
    } else if (
      (detail.internalStatus as BookingStatusEnum) ===
        BookingStatusEnum.DELIVERY_STARTED &&
      req.confirmDelivery
    ) {
      updateBookingForConfirmDelivery(detail, req);
    } else {
      throw new Error(
        `unknown status: ${detail.status}, req: ${JSON.stringify(req)}`
      );
    }
  } else {
    throw new Error('mock booking not found');
  }
  return {} as update_booking.Response;
};

// api
export const searchBookings = async (req: search_bookings.Request) => {
  if (useMock) {
    await sleep(500);
    return mockSearchBookings(req);
  }

  const res = await request({
    url: '/v1/search-bookings',
    method: 'post',
    data: req,
  });
  return res.data as search_bookings.Response;
};

export const getBookingDetail = async (req: get_booking_detail.Request) => {
  if (useMock) {
    await sleep(500);
    return mockGetBookingDetail(req);
  }

  const res = await request({
    url: '/v1/get-booking-detail',
    method: 'post',
    data: req,
  });
  return res.data as get_booking_detail.Response;
};

export const createBooking = async (req: book.Request) => {
  if (useMock) {
    await sleep(500);
    return mockCreateBooking(req);
  }

  const res = await request({
    url: '/v1/book',
    method: 'post',
    data: req,
  });
  return res.data as book.Response;
};

export const updateBooking = async (req: update_booking.Request) => {
  if (useMock) {
    await sleep(500);
    return mockUpdateBooking(req);
  }

  const res = await request({
    url: '/v1/update-booking',
    method: 'post',
    data: req,
  });
  return res.data as update_booking.Response;
};
